import { IMSObject, IMSId } from './ims-object';
import { parseStringPromise } from 'xml2js';
import { DynFormXML } from './dyn-form/dyn-form-xml';
import { LBSUtils } from '../_util/lbs-utils';
import { HasImplementationProperties } from './has-implementation-propeties';
import { ImplementationPropertiesUtil } from './implementation-properties-utils';

/**
 * (c) LBS logics
 *
 *     Mobile Business Technology GmbH
 *     4020 Linz, Austria
 *
 *     www.lbs-logics.com
 *
 *     created on 20190402 by Jan Brunner
 *     all rights reserved.
 */
 
export class DynForm extends IMSObject implements HasImplementationProperties {
  static async fromDto(dto: DynFormDTO, databag?: any) {
    let form: DynFormXML = await parseStringPromise(dto.formdata, {
      explicitArray: false,
    });
    const obj = new this(
      {
        imsId: dto.id,
        secondaryId: dto.secondaryid,
        objRef: 'ims/DynForm',
      },
      dto.label,
      dto.name,
      LBSUtils.convertStringBool(dto.showatfinishdialog, false),
      LBSUtils.convertStringBool(dto.finalizeday, false),
      dto.expiration,
      dto.extid,
      dto.formdata,
      form,
      dto.formtranslations ? JSON.parse(dto.formtranslations) : undefined,
    );

    if (databag) {
      obj.databag = databag;
    } else {
      obj.databag = dto;
    }
    return obj;
  }

  public get isBackSyncing(): boolean {
    console.log(
      'isBackSyncing called',
      this.databag,
      this.databag?.isbacksyncing,
    );
    return LBSUtils.convertStringBool(this.databag?.isbacksyncing, false);
  }

  public get isBackSyncingPdf(): boolean {
    return LBSUtils.convertStringBool(this.databag?.isbacksyncingpdf, false);
  }

  public get isBackSyncingXls(): boolean {
    return LBSUtils.convertStringBool(this.databag?.isbacksyncingxls, false);
  }

  public get dynFormTypeSyscode(): string {
    return this.databag?.dynformtypesyscode
  }

  /** APP4-948: tristate draft cloud sync (autosave on exit, claim on open) is active for this form */
  public get isCloudAutosaveActive(): boolean {
    return LBSUtils.convertStringBool(this.form?.form?.$?.cloudautosave, false);
  }

  /** APP4-948: "Am Auftrag hinterlegen" + takeover of service-job drafts is active for this form */
  public get isServiceJobDraftActive(): boolean {
    return LBSUtils.convertStringBool(this.form?.form?.$?.servicejobdraft, false);
  }

  public get implementationProperties(): ImplementationPropertiesUtil {
    return new ImplementationPropertiesUtil(this);
  }

  constructor(
    public readonly id: IMSId,
    label: string,
    public name: string,
    public showatfinishdialog: boolean,
    public finalizeday: boolean,
    public expiration: 'DAY' | 'WEEK' | 'JOB' | 'REQUEST',
    public extid: string,
    public formdata: string,
    public form: DynFormXML,
    public formTranslations?: DynFormTranslations,
  ) {
    super(id.imsId, id.secondaryId, label, 'ims/DynForm');
  }
}

/**
 * Form level translations shipped with the DynForm (dynformxml.formtranslations).
 *
 * Every top level key is a language holding the labels used inside the form, except for
 * `_self`, which carries translations of the form itself (e.g. `_self.name.fr`). The
 * backend reads `_self.name` for the file name of the generated document as well.
 */
export interface DynFormTranslations {
  _self?: {
    name?: Record<string, string>;
  };
  [lang: string]: any;
}

export interface DynFormDTO {
  id: number;
  secondaryid: number;
  label: string;
  name: string;
  showatfinishdialog: boolean;
  finalizeday: boolean;
  expiration: 'DAY' | 'WEEK' | 'JOB' | 'REQUEST';
  extid: string;
  formdata: string;
  isbacksyncing?: boolean;
  isbacksyncingpdf?: boolean;
  isbacksyncingxls?: boolean;
  editable?: boolean;
  formtranslations?: string;
}
